/**
 * web/src/pages/ApprovalQueue.tsx
 * UI-05: Role-filtered list of pending gates (server-side filter — admin
 * sees all). Sorted oldest-first. Warning indicator on items waiting >2hr;
 * 'Edit & Resubmit' button on rejected items. Routes to
 * /approvals/:workflowId/:stepIndex.
 *
 * 2026-08: the speaker family moved to its own action-first surface
 * (SpeakerApprovals.tsx, design_handoff_speaker_approvals), so this page is
 * SPONSOR-ONLY: it filters the shared /api/gates/pending result to everything
 * that isn't speaker_onboarding (incl. admin test workflows) so no gate is
 * orphaned by the split.
 *
 * The server's GET /api/gates/pending route is authoritative for visibility
 * (T-05-02-04 / CONTEXT.md "no role check is authoritative on the client").
 * If the route returns rejected steps in addition to waiting_approval, the
 * "Rejected — needs edits" section renders; otherwise it stays empty
 * (graceful degradation — server-contract change is out of scope here).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { KeyboardEvent, ReactNode } from 'react';
import { differenceInHours, formatDistanceToNow } from 'date-fns';
import { usePendingGates } from '../hooks/queries/usePendingGates';
import { useEvent } from '../hooks/useEvent';
import { useSetTopbar } from '../components/layout/Topbar';
import { RowCard } from '../components/shared/RowCard';
import { Dot } from '../components/shared/Dot';
import { Badge } from '../components/shared/Badge';
import { Button } from '../components/shared/Button';
import { Empty } from '../components/shared/Empty';
import { Skeleton } from '../components/shared/Skeleton';
import { Icon } from '../lib/icons';
import { cn } from '../lib/cn';
import { getStepCount } from '../lib/workflow-step-defs';
import { titleCase } from '../lib/format';
import type { PendingGate } from '../api/gates';
import { ReviewQueue } from '../components/approvals/ReviewQueue';
import type { ApprovalRow } from '../lib/approvalsDerive';
import { sponsorOpsRows, sponsorWelcomeRows } from '../lib/sponsorApprovalKinds';
import { RegenerateDialog, type RegenerateTarget } from '../components/approvals/RegenerateDialog';
import { useAuth } from '../hooks/useAuth';
import { useEventTemplates } from '../hooks/queries/useEventTemplates';
import { useSyncTemplates } from '../hooks/mutations/useSyncTemplates';
import { nextStepsCopyNote, type CopyNote } from '../lib/nextStepsCopy';

/**
 * Approval queue page (UI-05). Splits the server-returned gate list into
 * pending (status='waiting_approval') and rejected (status='rejected')
 * sections. Each row navigates to /approvals/:workflowId/:stepIndex on
 * click or Enter/Space.
 *
 * @sideEffects subscribes to ['gates','pending'] React Query; publishes topbar content
 * @idempotent yes — multiple mounts share the cache entry
 */
/** Regenerate targets for a batch banner's rows. */
function regenTargetsFor(rows: ApprovalRow[], label: string): RegenerateTarget[] {
  return rows.map((r) => ({
    workflowId: r.gate.workflowId,
    stepIndex: r.gate.stepIndex,
    label: `${label} · ${r.speaker}`,
  }));
}

/**
 * "N <noun>s ready" strip above the gate list: Rebuild drafts + Review & send
 * all for one kind of sponsor email. Renders nothing when `rows` is empty.
 */
function BatchBanner({
  rows,
  noun,
  docName,
  onRebuild,
  onReview,
  note,
  extraAction,
}: {
  rows: ApprovalRow[];
  noun: string;
  docName: string;
  onRebuild: () => void;
  onReview: () => void;
  /** Optional status line under the description (the Next Steps copy state). */
  note?: CopyNote | null;
  /** Optional button before Rebuild drafts (Sync copy). */
  extraAction?: ReactNode;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="mb-5 px-4 py-3 flex items-center gap-4 border border-border rounded-xl bg-bg-soft">
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-text">
          {rows.length} {noun}
          {rows.length === 1 ? '' : 's'} ready
        </div>
        <div className="text-[12px] text-text-subtle">
          Step through them one after another — check each draft, then Approve &amp; send or skip.
          Changed the {docName} or the CCs? Rebuild drafts first.
        </div>
        {note && (
          <div
            className={`mt-1 text-[12px] ${note.tone === 'warn' ? 'text-warn' : 'text-text-subtle'}`}
          >
            {note.text}
          </div>
        )}
      </div>
      {extraAction}
      <Button
        variant="ghost"
        onClick={onRebuild}
        title={`Re-create every waiting ${noun} from the current ${docName} and settings — edits made to those drafts are dropped; nothing is sent`}
      >
        Rebuild drafts
      </Button>
      <Button variant="primary" onClick={onReview}>
        Review &amp; send all
      </Button>
    </div>
  );
}

export function ApprovalQueue() {
  const navigate = useNavigate();
  const { eventId, event, isPending: eventsPending } = useEvent();
  const { data: gates, isPending, error } = usePendingGates(eventId ?? undefined);
  // Batch pass over pending welcome or Exhibit Next Steps emails (FP Connect
  // 2026-10, FPC27): the shared speaker ReviewQueue, one email at a time, each
  // approve = the normal send.
  const [queue, setQueue] = useState<ApprovalRow[] | null>(null);
  // "Rebuild drafts": re-render every waiting welcome (or next steps) email from
  // the event's current Doc + settings (after a copy or CC change). Nothing sends.
  const [regenTargets, setRegenTargets] = useState<RegenerateTarget[] | null>(null);
  const { me } = useAuth();
  // Next Steps copy state for the next steps banner (auto-synced from the Doc
  // ~10 min after the last edit; "Sync copy" pulls it now). Admin, operations
  // and sales_ops may sync.
  const { data: templates } = useEventTemplates(eventId ?? undefined);
  const nextStepsTemplate = templates?.find((t) => t.docKey === 'opsExhibit');
  const syncCopy = useSyncTemplates(eventId ?? '');
  const canSyncCopy = me?.role === 'admin' || me?.role === 'operations' || me?.role === 'sales_ops';

  useSetTopbar({
    title: 'Sponsor Approvals',
    // Scoped to the active event (like the Workflows tab), so the subtitle
    // names the event rather than the old cross-event 'All gates'.
    subtitle: event ? `${event.code} · ${event.name}` : undefined,
  });

  // One shared query serves both areas; speaker gates render on the dedicated
  // SpeakerApprovals surface, so this page keeps everything else.
  const rows = (gates ?? []).filter((g) => g.workflow.type !== 'speaker_onboarding');
  const pending = rows.filter((g) => g.status === 'waiting_approval');
  const rejected = rows.filter((g) => g.status === 'rejected');

  // The gates query is disabled until an event resolves (enabled: eventId != null),
  // so its `isPending` stays true forever for a user with no accessible events.
  // Gate the skeleton on the RIGHT loading signal: while the event is resolving
  // show the skeleton off the events query; once an event is selected show it
  // off the gates query. A user with zero events (eventId stays null after the
  // list loads) falls through to the empty state instead of a perpetual skeleton.
  const showSkeleton = eventId == null ? eventsPending : isPending;

  if (showSkeleton) {
    return (
      <div className="max-w-[1100px] flex flex-col gap-2.5">
        <Skeleton variant="card" />
        <Skeleton variant="card" />
        <Skeleton variant="card" />
      </div>
    );
  }

  if (error) {
    return (
      <Empty
        icon={<Icon name="alert" size={20} />}
        title="Couldn't load approvals"
        description={error instanceof Error ? error.message : ''}
      />
    );
  }

  if (pending.length === 0 && rejected.length === 0) {
    return (
      <Empty
        icon={<Icon name="approval" size={20} />}
        title="No approvals waiting on you"
        description="Pending sponsor gates appear here in real time."
      />
    );
  }

  const openRow = (g: PendingGate) => navigate(`/approvals/${g.workflowId}/${g.stepIndex}`);

  const welcomeRows = sponsorWelcomeRows(pending);
  const opsRows = sponsorOpsRows(pending);

  return (
    <div className="max-w-[1100px]">
      <BatchBanner
        rows={welcomeRows}
        noun="welcome email"
        docName="welcome Doc"
        onRebuild={() => setRegenTargets(regenTargetsFor(welcomeRows, 'Welcome email'))}
        onReview={() => setQueue(welcomeRows)}
      />
      <BatchBanner
        rows={opsRows}
        noun="next steps email"
        docName="next steps Doc"
        note={nextStepsCopyNote(
          nextStepsTemplate,
          opsRows.map((r) => r.gate.updatedAt),
        )}
        extraAction={
          eventId && canSyncCopy ? (
            <Button
              variant="ghost"
              disabled={syncCopy.isPending}
              onClick={() => syncCopy.mutate()}
              title="Pull the latest Next Steps copy from the Google Doc now (Pixel also does this on its own about 10 minutes after the last edit); nothing is sent"
            >
              {syncCopy.isPending ? 'Syncing…' : 'Sync copy'}
            </Button>
          ) : null
        }
        onRebuild={() => setRegenTargets(regenTargetsFor(opsRows, 'Next steps email'))}
        onReview={() => setQueue(opsRows)}
      />
      <RegenerateDialog
        targets={regenTargets}
        userId={me?.id}
        onClose={() => setRegenTargets(null)}
      />
      {queue && <ReviewQueue items={queue} onExit={() => setQueue(null)} />}
      {pending.length > 0 && (
        <Section title="Pending">
          {pending.map((g) => (
            <GateRow
              key={`${g.workflowId}-${g.stepIndex}`}
              gate={g}
              variant="pending"
              onOpen={() => openRow(g)}
            />
          ))}
        </Section>
      )}
      {rejected.length > 0 && (
        <Section title="Rejected — needs edits">
          {rejected.map((g) => (
            <GateRow
              key={`${g.workflowId}-${g.stepIndex}`}
              gate={g}
              variant="rejected"
              onOpen={() => openRow(g)}
            />
          ))}
        </Section>
      )}
    </div>
  );
}

/**
 * Section header — uppercase mini-title with a hairline divider extending to the right.
 *
 * @sideEffects none — purely visual.
 */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-7">
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {title}
        </h2>
        <span className="flex-1 h-px bg-border" />
      </div>
      <div className="flex flex-col gap-2.5">{children}</div>
    </section>
  );
}

/**
 * Single gate row. Whole row is clickable (RowCard hover). Pending rows show
 * waiting dot + Review button; rejected rows show failed dot + Edit & Resubmit
 * button + inline rejection-reason callout. Wait-time pill turns amber when
 * the gate has been waiting more than 2 hours (UI-05 ⚠️).
 *
 * @sideEffects calls onOpen on click / Enter / Space
 */
function GateRow({
  gate: g,
  variant,
  onOpen,
}: {
  gate: PendingGate;
  variant: 'pending' | 'rejected';
  onOpen: () => void;
}) {
  const hours = differenceInHours(new Date(), new Date(g.createdAt));
  const isOld = variant === 'pending' && hours > 2;
  const stepName = titleCase(g.stepId);
  const totalSteps = getStepCount(g.workflow.type);
  const company = g.workflow.displayName;

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <RowCard
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={handleKey}
      aria-label={`Review gate: ${company}, step ${g.stepIndex + 1}`}
    >
      <Dot
        status={variant === 'rejected' ? 'failed' : 'waiting'}
        pulse={variant !== 'rejected'}
        size={12}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-semibold text-text truncate">{company}</span>
          {g.gateRole && <Badge variant="role">{g.gateRole.replace('_', ' ')}</Badge>}
          <span className="text-[12px] text-text-faint">·</span>
          <span className="text-[12px] text-text-muted">{g.workflow.event.code}</span>
        </div>
        <div className="text-[13px] text-text-muted truncate mt-0.5">
          Step {g.stepIndex + 1} of {totalSteps}: {stepName}
        </div>
        {variant === 'rejected' && g.rejectionReason && (
          <div className="mt-2 px-3 py-1.5 bg-failed-soft border border-failed-border rounded-md text-[12px] text-failed">
            <strong>
              Rejected
              {g.approvedByName ? ` by ${g.approvedByName}` : ''}:
            </strong>{' '}
            {g.rejectionReason}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <span
          className={cn(
            'text-[11px] tabular-nums',
            isOld
              ? 'text-waiting bg-waiting-soft border border-waiting-border rounded-md px-2 py-0.5 font-medium inline-flex items-center'
              : 'text-text-subtle',
          )}
        >
          {isOld && <Icon name="alert" size={11} className="inline mr-1" />}
          {formatDistanceToNow(new Date(g.createdAt), { addSuffix: true })}
        </span>
        <Button
          size="sm"
          variant={variant === 'rejected' ? 'default' : 'primary'}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          {variant === 'rejected' ? 'Edit & Resubmit' : 'Review'}
          <Icon name="chevron-right" size={12} />
        </Button>
      </div>
    </RowCard>
  );
}
